<template>
    <div v-if="!showThankYou">
        <div class="d-md-flex">
            <div class="me-md-1">{{ $t('reviewForm.title') }}</div>
            <div>
                <strong>{{ serviceName }}</strong>
            </div>
        </div>
        <div class="row mb-3">
            <div v-if="serviceOfficeName" class="col-3">{{ $t('reviewForm.service') }}</div>
            <div v-if="serviceOfficeName" class="col-3">
                <strong>{{ serviceOfficeName }}</strong>
            </div>
            <div v-if="applied" class="col-3">{{ $t('reviewForm.applied') }}</div>
            <div v-if="applied" class="col-3">
                <strong>{{ applied }}</strong>
            </div>
        </div>

        <form @submit.prevent="saveComment">
            <div class="row">
                <div class="col-md-6">
                    <fieldset class="form-group mb-3">
                        <label class="form-label" for="form-input-1"
                            >{{ $t('formsElement.fullName') }}: <span class="text-danger">*</span></label
                        >
                        <input
                            id="form-input-1"
                            v-model="name"
                            class="form-control form-control--silver"
                            data-cy="review-name"
                            type="text"
                            required
                        />
                    </fieldset>
                </div>
                <div class="col-md-6">
                    <fieldset class="form-group mb-3">
                        <label class="form-label" for="form-input-2"
                            >{{ $t('formsElement.email') }}: <span class="text-danger">*</span></label
                        >
                        <input
                            id="form-input-2"
                            v-model="email"
                            type="email"
                            required
                            :disabled="defaultEmail.length > 0"
                            class="form-control form-control--silver"
                            data-cy="review-email"
                        />
                    </fieldset>
                </div>
            </div>

            <fieldset class="form-group mb-4">
                <label class="form-label">{{ $t('reviewForm.yourRating') }} <span class="text-danger">*</span></label>
                <div class="d-flex">
                    <AddRating :rating="rating" :more-info="false" class="template-stars" size="star-xl" @set-rating="rateChange" />
                </div>
            </fieldset>

            <div class="row">
                <div class="col-12 col-md-6">
                    <fieldset class="form-group">
                        <label class="form-label">
                            <span class="text-success">{{ $t('reviewForm.for') }}</span>
                        </label>

                        <template v-for="(object, index) in pros" :key="index">
                            <div class="mb-3 input-group-bordered input-group">
                                <input
                                    :ref="`pros-${index}`"
                                    v-model="object.value"
                                    class="input-prepend-icon-plus form-control form-control--silver"
                                    data-cy="review-plus"
                                    type="text"
                                    maxlength="256"
                                    @keydown.enter.prevent="prosChangedByEnter(index)"
                                    @change="prosChanged(index)"
                                />
                                <div
                                    v-if="pros.length > 1 && !(pros.length === index + 1 && object.value.length === 0)"
                                    class="bg-grey-light cursor-point border-0 input-group-text"
                                >
                                    <i class="ico ico-close filter-grayscale-light cursor-point" @click="deletePros(index)"></i>
                                </div>
                            </div>
                        </template>
                    </fieldset>
                </div>
                <div class="col-12 col-md-6">
                    <fieldset class="form-group">
                        <label class="form-label"
                            ><span class="text-danger">{{ $t('reviewForm.against') }}</span></label
                        >

                        <template v-for="(object, index) in cons" :key="index">
                            <div class="mb-3 input-group-bordered input-group">
                                <input
                                    :ref="`cons-${index}`"
                                    v-model="object.value"
                                    class="input-prepend-icon-minus form-control form-control--silver"
                                    data-cy="review-minus"
                                    type="text"
                                    maxlength="256"
                                    @keydown.enter.prevent="consChangedByEnter(index)"
                                    @change="consChanged(index)"
                                />
                                <div
                                    v-if="cons.length > 1 && !(cons.length === index + 1 && object.value.length === 0)"
                                    class="bg-grey-light cursor-point border-0 input-group-text"
                                >
                                    <i class="ico ico-close filter-grayscale-light cursor-point" @click="deleteCons(index)"></i>
                                </div>
                            </div>
                        </template>
                    </fieldset>
                </div>
            </div>

            <fieldset v-if="questionRatingValues.length > 0" class="form-group mb-3">
                <label class="form-label">{{ $t('reviewForm.evaluation') }}</label>
                <div class="row">
                    <div v-for="(questionRatingValue, index) in questionRatingValues" :key="index" class="col-12 col-lg-6 my-2">
                        <div class="row align-items-lg-center">
                            <span class="col-12 col-lg-6">
                                {{ questionRatingValue.name }}
                            </span>
                            <AddRating
                                :rate-id="questionRatingValue.id"
                                :rating="questionRatingValue.rate"
                                :product-id="objectId"
                                class="col-12 col-lg-6"
                                @set-rating="setQuestionRatingValues"
                            />
                        </div>
                    </div>
                </div>
            </fieldset>

            <fieldset class="form-group mb-3">
                <label class="form-label">{{ $t('reviewForm.evaluationText') }} <span class="text-danger">*</span></label>
                <textarea
                    v-model="text"
                    class="form-control form-control--silver"
                    data-cy="review-text"
                    required
                    rows="2"
                    wrap="soft"
                ></textarea>
            </fieldset>

            <div v-if="defaultEmail.length <= 0" class="form-check">
                <input id="conditions" class="form-check-input" data-cy="conditions" type="checkbox" value="" name="conditions" required />
                <label class="form-check-label" for="conditions">
                    <b>
                        {{ $t('reviewForm.personalData.text') }}
                        <a :href="$t('reviewForm.personalData.url')" target="_blank" class="text-primary">
                            {{ $t('reviewForm.personalData.link') }}
                        </a>
                    </b>
                    (<span class="text-danger">*</span>)
                </label>
            </div>

            <div class="row">
                <div class="col-12 col-md-6 mb-3 mb-md-0 py-md-2">
                    <span>
                        {{ $t('reviewForm.select.text') }}
                        (<span class="text-danger">*</span>)
                        {{ $t('reviewForm.select.textEnd') }}
                    </span>
                </div>
                <div class="col-12 col-md-6 text-center text-md-end">
                    <button type="submit" class="btn btn-primary btn-150 d-md-inline-block">
                        {{ $t('reviewForm.send') }}
                    </button>
                </div>
            </div>

            <div v-if="error" class="row">
                <div class="col-12 flash-message mt-2">
                    {{ errorText !== '' ? errorText : $t('reviewForm.error') }}
                </div>
            </div>
        </form>

        <Loader v-if="loading" />
    </div>
    <div v-else>
        <div class="alert alert-success">
            {{ $t('reviewForm.thankYou') }}
        </div>
    </div>
</template>

<script lang="ts">
import AddRating from '~/components/objects/AddRating.vue'
import Loader from '~/components/ui/Loader.vue'
import { useAuthStore } from '~/stores/auth'
import GeneralResponse from '~/models/Api/GeneralResponse'

export default {
    components: {
        AddRating,
        Loader,
    },

    props: {
        objectId: {
            type: Number,
            required: true,
        },
        type: {
            type: Number,
            required: true,
        },
        defaultName: {
            type: String,
            default: '',
        },
        defaultEmail: {
            type: String,
            default: '',
        },
        questionRatings: {
            type: Array,
            default: () => [],
        },
        serviceName: {
            type: String,
            default: '',
        },
        serviceOfficeName: {
            type: String,
            default: '',
        },
        feedbackHash: {
            type: String,
            default: '',
        },
        applied: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            rating: 5,
            text: '',
            name: '',
            email: '',
            pros: [{ value: '' }],
            cons: [{ value: '' }],
            questionRatingValues: [] as { id: number; name: string; rate: number }[],
            loading: false,
            error: false,
            errorText: '',
            showThankYou: false,
        }
    },

    mounted() {
        this.name = this.defaultName
        this.email = this.defaultEmail

        this.questionRatings.forEach((questionRating: { id: number; text: string }) => {
            this.questionRatingValues.push({
                id: questionRating.id,
                name: questionRating.text,
                rate: 0,
            })
        })
    },

    methods: {
        rateChange(rate: number): void {
            // console.log(rate)
            this.rating = rate
        },

        setQuestionRatingValues(rate: number, id: number): void {
            this.questionRatingValues.forEach((questionRating: { id: number; name: string; rate: number }) => {
                if (questionRating.id === id) {
                    questionRating.rate = rate
                }
            })
        },

        async saveComment(): Promise<void> {
            this.loading = true
            this.error = false
            const authStore = useAuthStore()

            const data = {
                accessToken: authStore.accessToken,
                objectId: this.objectId,
                type: this.type,
                name: this.name,
                email: this.email,
                text: this.text,
                rating: this.rating,
                questionRatings: this.questionRatingValues,
                pros: this.pros.map(({ value }: { value: string }) => value),
                cons: this.cons.map(({ value }: { value: string }) => value),
                feedbackHash: this.feedbackHash,
            }

            let response: GeneralResponse<{}> | null = null

            try {
                response = await this.$repository.addComment(data)

                if (!response || !response.result) {
                    this.errorText = response?.message
                    this.error = true
                } else if (response) {
                    this.showThankYou = true
                    // useNuxtApp().$bus.$emit('hideModal', 'modal-form-review')
                    this.error = false
                }
                this.loading = false
            } catch (err) {
                this.error = true
                this.loading = false
            }

            this.loading = false
        },

        deletePros(index: number): void {
            this.pros.splice(index, 1)
        },

        prosChanged(index: number): void {
            if (this.pros.length - 1 === index && this.pros[index]?.value.length > 0) {
                this.pros.push({ value: '' })
            }
        },

        prosChangedByEnter(index: number): void {
            if (this.pros.length === index + 1 && this.pros[index]?.value.length > 0) {
                this.pros.push({ value: '' })
                this.$nextTick(function () {
                    this.$refs[`pros-${index + 1}`][0].focus()
                })
            }
        },

        deleteCons(index: number): void {
            this.cons.splice(index, 1)
        },

        consChanged(index: number): void {
            if (this.cons.length - 1 === index && this.cons[index]?.value.length > 0) {
                this.cons.push({ value: '' })
            }
        },

        consChangedByEnter(index: number): void {
            if (this.cons.length === index + 1 && this.cons[index]?.value.length > 0) {
                this.cons.push({ value: '' })
                this.$nextTick(function () {
                    this.$refs[`cons-${index + 1}`][0].focus()
                })
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.form-check-input {
    margin-top: 2px;
}

.custom-control-label {
    line-height: 24px;
}

.input-group-bordered {
    border-radius: 10px;
}
.flash-message {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    border-radius: 5px;
    padding: 0.7rem 1rem;
    font-weight: 500;
    background-color: $color-alert-light;
    border: 1px solid $color-alert;
    color: $color-dark;
}

.alert {
    background: $color-white;
    position: relative;
    box-shadow: inset 0 0 0 1px rgba($color-white, 0.2);
    padding-left: 50px;

    &-success {
        padding: 15px;
        border: 0;
        background: $color-green-dark;
        color: $color-white;
    }
}
</style>
